import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import PennText from "@/components/ui/text/PennText";
import type { SelectorProduct } from "@/api/filter";
import pemserterImg from "@/assets/images/brand-logo/pemserter.png";
import profilImg from "@/assets/images/brand-logo/profil.png";
import haegerImg from "@/assets/images/brand-logo/haeger.png";
import mdsImg from "@/assets/images/brand-logo/mds.png";
import shrexImg from "@/assets/images/brand-logo/shrex.png";

const SHOW_DELAY_MS = 120;
const HIDE_DELAY_MS = 150;

type ProductHoverCardProps = {
  product: SelectorProduct;
  children: ReactNode;
};

// Mirrors the POC's `kcResponsibilityToLogo`: maps a
// product's `responsibility` (product-line code) to its brand logo so the hover
// card shows e.g. the "Haeger by PEM" lockup, matching the POC card.
function responsibilityLogo(
  responsibility: string | null,
): { src: string; alt: string } | null {
  const r = (responsibility ?? "").trim().toLowerCase();
  if (!r) return null;
  if (r.includes("haeger")) return { src: haegerImg, alt: "Haeger by PEM" };
  if (r.includes("mds")) return { src: mdsImg, alt: "MDS Autoriv" };
  if (r.includes("pem")) return { src: pemserterImg, alt: "PEMSERTER" };
  if (r.includes("profil")) return { src: profilImg, alt: "PROFIL" };
  if (r.includes("sherex") || r.includes("shrex"))
    return { src: shrexImg, alt: "SHEREX" };
  return null;
}

export const ProductHoverCard = ({ product, children }: ProductHoverCardProps) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Fixed-position the card and clamp it to the viewport so it never
  // overflows for triggers near the right/bottom edges; flips above the
  // trigger when there is not enough room below.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const anchor = anchorRef.current;
    const card = cardRef.current;
    if (!anchor || !card) return;
    const rect = anchor.getBoundingClientRect();
    const { width, height } = card.getBoundingClientRect();
    const margin = 16;
    const left = Math.max(
      margin,
      Math.min(rect.left, window.innerWidth - width - margin),
    );
    let top = rect.bottom + 8;
    if (
      top + height > window.innerHeight - margin &&
      rect.top - height - 8 >= margin
    ) {
      top = rect.top - height - 8;
    }
    setPos({ left, top });
  }, [open]);

  const clearTimers = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const show = () => {
    clearTimers();
    showTimerRef.current = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
  };
  const hide = () => {
    clearTimers();
    hideTimerRef.current = setTimeout(() => setOpen(false), HIDE_DELAY_MS);
  };

  const brand = responsibilityLogo(product.responsibility);

  return (
    <span
      ref={anchorRef}
      className="inline-block"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open && (
        <div
          ref={cardRef}
          role="tooltip"
          onMouseEnter={show}
          onMouseLeave={hide}
          style={pos ? { left: pos.left, top: pos.top } : { visibility: "hidden" }}
          className="fixed z-50 w-4xl max-w-[calc(100vw-2rem)] rounded-2xl border border-gray-200 bg-white shadow-soft p-4"
        >
          <div className="flex gap-4">
            {product.imageLink ? (
              <img
                src={product.imageLink}
                alt={product.name}
                className="w-28 h-28 object-contain rounded-xl border border-gray-100 flex-shrink-0 bg-gray-50"
              />
            ) : (
              <div className="w-28 h-28 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                <PennText as="span" size="xs" color="blue_grey">No image</PennText>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <PennText as="p" size="base" color="blue_grey1" font="semibold">
                {product.link ? (
                  <a
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline hover:text-blue-600"
                  >
                    {product.name}
                  </a>
                ) : (
                  product.name
                )}
              </PennText>
              <PennText as="p" size="xs" color="blue_grey" font="semibold" className="mt-2">
                Key Characteristics
              </PennText>
              {product.keyCharacteristics.length > 0 ? (
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  {product.keyCharacteristics.map((kc, i) => (
                    <li key={i}>
                      <PennText as="span" size="xs" color="blue_grey">{kc}</PennText>
                    </li>
                  ))}
                </ul>
              ) : (
                <PennText as="p" size="xs" color="blue_grey" className="mt-2">
                  No key characteristics available.
                </PennText>
              )}
            </div>
          </div>
          {brand && (
            <div className="mt-3 flex justify-end border-t border-gray-100 pt-3">
              <img
                src={brand.src}
                alt={brand.alt}
                className="h-8 w-auto object-contain"
              />
            </div>
          )}
        </div>
      )}
    </span>
  );
};
